import { useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { lineEditorOf } from './assembly';
import { FieldInput, inputFieldOf } from './FieldInput';
import { LineEditorDialog } from './LineEditor';
import { formatValue, type FieldSummary } from './format';
import { useAppServices, type ViewCommand } from './ViewContext';

/**
 * Saisie de la charge utile d'une action (ADR-064 §3) : les champs viennent de la définition
 * déclarée que la vue a résolue ; les valeurs pré-remplies (`inputs`) s'affichent en lecture seule.
 * La commande part telle quelle : le serveur la valide, l'autorise et répond champ par champ.
 */
/** Affichage d'une valeur pré-remplie avec le libellé de son énumération. */
function summaryOf(definition: ViewCommand['payload'][number], labels: Readonly<Record<string, Readonly<Record<string, string>>>> | undefined): FieldSummary {
  const summary = { field_id: definition.field_id, label: definition.label ?? { fr: definition.field_id }, type: definition.type } as FieldSummary;
  if (labels !== undefined) summary.enum_labels = labels as Record<string, Record<string, string>>;
  if (definition.unit !== undefined) summary.unit = definition.unit;
  return summary;
}

export function CommandDialog({
  command,
  prefilled,
  target,
  expectedVersion,
  subject,
  onDone,
  onClose,
  intro,
  onSubmitPayload,
}: {
  command: ViewCommand;
  prefilled: Readonly<Record<string, unknown>>;
  target: { type: string; id: string } | null;
  expectedVersion?: number;
  subject?: { type: string; id: string };
  onDone: () => void;
  onClose: () => void;
  /** Texte en tête du formulaire (action de masse : nombre d'éléments visés). */
  intro?: ReactNode;
  /**
   * Action de masse (ADR-096 §4) : la charge utile saisie est rendue à l'appelant, qui lance
   * l'opération du Work Engine ; rien n'est dispatché ici.
   */
  onSubmitPayload?: (payload: Record<string, unknown>) => Promise<void>;
}) {
  // Éditeur de lignes déclaré par la vue pour un champ tableau (ADR-136 §2) : saisie ligne par ligne au lieu d'un champ json.
  const lineEditor = onSubmitPayload === undefined ? lineEditorOf(command) : undefined;
  if (lineEditor !== undefined) {
    return <LineEditorDialog command={command} spec={lineEditor} prefilled={prefilled} target={target} {...(expectedVersion === undefined ? {} : { expectedVersion })} onDone={onDone} onClose={onClose} intro={intro} />;
  }
  return <PayloadDialog {...{ command, prefilled, target, onDone, onClose, intro }} {...(expectedVersion === undefined ? {} : { expectedVersion })} {...(subject === undefined ? {} : { subject })} {...(onSubmitPayload === undefined ? {} : { onSubmitPayload })} />;
}

function PayloadDialog({
  command,
  prefilled,
  target,
  expectedVersion,
  subject,
  onDone,
  onClose,
  intro,
  onSubmitPayload,
}: Parameters<typeof CommandDialog>[0]) {
  const services = useAppServices();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ category: string; message: string; fields: Record<string, string> } | null>(null);

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { ...prefilled };
      for (const [key, value] of Object.entries(values)) if (value !== null && value !== undefined) payload[key] = value;
      const input: Parameters<typeof services.client.command>[1] = { payload };
      if (target !== null) input.target = target;
      if (expectedVersion !== undefined) input.expected_version = expectedVersion;
      if (onSubmitPayload !== undefined) await onSubmitPayload(payload);
      else await services.client.command(command.command_name, input);
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const fields: Record<string, string> = {};
        for (const entry of caught.fieldErrors) fields[entry.field.replace(/^payload\./, '')] = entry.message;
        setError({ category: caught.category, message: caught.message, fields });
      } else setError({ category: 'INTERNAL_ERROR', message: String(caught), fields: {} });
    } finally {
      setSaving(false);
    }
  };

  const denied = error !== null && (error.category === 'AUTHORIZATION_ERROR' || error.category === 'POLICY_REJECTION');
  return (
    <Dialog title={text(command.label)} open onClose={onClose} size="md">
      <form
        className="ay-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {intro}
        {error !== null && (
          <p className={`ay-banner ${denied ? 'ay-banner--warning' : 'ay-banner--danger'}`} role="alert">
            {denied ? `Action refusée : ${error.message}` : error.message}
          </p>
        )}
        {command.payload.map((definition) => {
          const field = inputFieldOf(definition);
          if (Object.hasOwn(prefilled, definition.field_id)) {
            return (
              <div key={definition.field_id} className="ay-field">
                <span className="ay-field__label">{text(definition.label, 'fr', definition.field_id)}</span>
                <output className="ay-field__readonly">{formatValue(prefilled[definition.field_id], summaryOf(definition, field.enum_labels))}</output>
              </div>
            );
          }
          const fieldProps: Parameters<typeof FieldInput>[0] = { field, value: values[definition.field_id] ?? null, onChange: (value) => setValues((current) => ({ ...current, [definition.field_id]: value })), disabled: saving };
          const message = error?.fields[definition.field_id];
          if (message !== undefined) fieldProps.error = message;
          if (subject !== undefined) fieldProps.subject = subject;
          return <FieldInput key={definition.field_id} {...fieldProps} />;
        })}
        <DialogActions cancel={{ label: 'Annuler', onClick: onClose, disabled: saving }} primary={{ label: 'Valider', type: 'submit', busy: saving }} />
      </form>
    </Dialog>
  );
}
